/**
 * Admin → Gebruikersbeheer → Groepen: de centrale groepen die de app voor autorisatie gebruikt (eerst: MT).
 *
 * Het MT is beoordelaar van elke vacature (werving). Wijzigen kan alleen met admin_settings (bewerken); de server
 * controleert dat zelf en legt elke wijziging vast (zie "Laatste wijzigingen"). Toevoegen kan alleen voor
 * bestaande, actieve gebruikers; de laatste persoon kan niet uit de groep. Vangnet: verwijderen vraagt eerst
 * bevestiging, toevoegen en verwijderen zijn met "Ongedaan maken" terug te draaien.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { bevestig } from '../../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../../utils/ongedaan';

async function vraag(pad, opties = {}) {
  const r = await authFetch(`${BACKEND_URL}${pad}`, {
    ...opties,
    headers: { 'Content-Type': 'application/json', ...(opties.headers || {}) },
    body: opties.body !== undefined ? JSON.stringify(opties.body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : (d.error || `Fout ${r.status}`));
  return d;
}

const api = {
  overzicht: () => vraag('/Api/groepen'),
  voegToe: (groep, email) => vraag(`/Api/groepen/${groep}/leden`, { method: 'POST', body: { email } }),
  verwijder: (groep, email) => vraag(`/Api/groepen/${groep}/leden/${encodeURIComponent(email)}`, { method: 'DELETE' }),
};

function datum(iso) {
  if (!iso) return '';
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function GroepKaart({ g, onWijzig }) {
  const [email, setEmail] = useState('');
  const [bezig, setBezig] = useState(false);

  const voegToe = async () => {
    const e = email.trim().toLowerCase();
    if (!e) return;
    setBezig(true);
    try {
      const r = await api.voegToe(g.sleutel, e);
      setEmail('');
      if (r.gewijzigd) biedOngedaanAan(`${e} toegevoegd aan ${g.naam}`, async () => { await api.verwijder(g.sleutel, e); onWijzig(); });
      else toast(`${e} zat al in ${g.naam}`);
      onWijzig();
    } catch (err) { toast.error(err.message); } finally { setBezig(false); }
  };

  const verwijder = async (lid) => {
    if (!(await bevestig({
      titel: `${lid.naam || lid.email} uit ${g.naam} halen?`,
      tekst: `${g.naam} geeft rechten in de app (o.a. beoordelaar van elke vacature in Werving). Dat vervalt meteen.`,
      knop: 'Uit de groep', gevaarlijk: true,
    }))) return;
    try {
      await api.verwijder(g.sleutel, lid.email);
      biedOngedaanAan(`${lid.email} uit ${g.naam}`, async () => { await api.voegToe(g.sleutel, lid.email); onWijzig(); });
      onWijzig();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <Box sx={{ border: '1px solid #eceef1', borderRadius: 3, p: 2, mb: 2, bgcolor: '#fff' }}>
      <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>{g.naam}</Typography>
      {g.omschrijving && <Typography sx={{ fontSize: 13, color: '#5f6b7a', mb: 1.5 }}>{g.omschrijving}</Typography>}
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {g.leden.map((l) => (
          <Chip key={l.email} label={l.naam || l.email} title={`${l.email}${l.toegevoegd_door ? ` · toegevoegd door ${l.toegevoegd_door}` : ''}`}
            onDelete={() => verwijder(l)} />
        ))}
        {!g.leden.length && <Typography sx={{ fontSize: 13, color: '#5f6b7a' }}>Nog niemand.</Typography>}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField size="small" label="Toevoegen (e-mailadres)" placeholder="naam@fullframe.nl" value={email}
          onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }} sx={{ minWidth: 280 }} />
        <Button variant="outlined" onClick={voegToe} disabled={!email.trim() || bezig} sx={{ textTransform: 'none' }}>Toevoegen</Button>
      </Stack>
      {(g.rol_management_niet_in_groep || []).length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Rol "management" maar niet in deze groep: {g.rol_management_niet_in_groep.map((x) => x.naam || x.email).join(', ')}.
          Tot oktober 2026 telde die rol als MT; nu telt alleen deze groep. Voeg ze toe als ze MT zijn.
        </Alert>
      )}
    </Box>
  );
}

export default function Groepen() {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const laad = useCallback(() => api.overzicht().then((d) => { setData(d); setFout(null); }).catch((e) => setFout(e.message)), []);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  if (fout) return <Alert severity="warning">{fout}</Alert>;
  if (!data) return <CircularProgress size={20} />;
  return (
    <Box sx={{ maxWidth: 820 }}>
      <Typography sx={{ fontSize: 13.5, color: '#5f6b7a', mb: 2 }}>
        Groepen die de app voor autorisatie gebruikt. Alleen beheerders (admin-instellingen bewerken) kunnen ze wijzigen;
        elke wijziging wordt vastgelegd.
      </Typography>
      {data.groepen.map((g) => <GroepKaart key={g.sleutel} g={g} onWijzig={laad} />)}
      {data.audit.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>LAATSTE WIJZIGINGEN</Typography>
          {data.audit.map((a, i) => (
            <Typography key={i} sx={{ fontSize: 12.5, color: '#5f6b7a' }}>
              {datum(a.op)} · {a.email} {a.actie} ({a.groep.toUpperCase()}) door {a.door}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
